import { TextField, Autocomplete } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { INeighborhood, INeighborhoodRes } from '../../models';
import { getCommonNeighborhoods } from '../../fetch/lookups';

interface INeighborhoodAutocompleteDropdownProps {
  value?: string | number;
  handleChange: (val: string | number) => void;
  isDisabled?: boolean;
  setNeighborhoods?: (val: INeighborhood[]) => void;
  apiParams?: any;
  isRequired?: boolean;
  disableClearable?: boolean;
}

export const NeighborhoodAutocompleteDropdown: FC<INeighborhoodAutocompleteDropdownProps> = ({
  isDisabled,
  isRequired,
  value,
  handleChange,
  setNeighborhoods,
  apiParams,
  disableClearable = true
}) => {
  const [autocompleteValue, setAutocompleteValue] = useState<INeighborhood | null>(null);
  const { isLoading: isLoadingNeighborhoods, data: neighborhoodData } = useQuery<
    INeighborhoodRes,
    Error
  >(['neighborhood-filters', apiParams], () => getCommonNeighborhoods(apiParams), {
    notifyOnChangeProps: 'tracked',
    onSuccess: d => setNeighborhoods?.(d.records)
  });
  const neighborhoods = useMemo(() => neighborhoodData?.records ?? [], [neighborhoodData]);

  useEffect(() => {
    if (value) {
      setAutocompleteValue(neighborhoods.find(n => n.neighborhoodId === value) ?? null);
    } else {
      setAutocompleteValue(null);
    }
  }, [neighborhoods, value]);

  return (
    <Autocomplete
      disableClearable={disableClearable}
      value={autocompleteValue}
      onChange={(event, newValue: INeighborhood) => {
        setAutocompleteValue(newValue);
        handleChange(newValue?.neighborhoodId);
      }}
      disabled={isLoadingNeighborhoods || isDisabled}
      selectOnFocus
      handleHomeEndKeys
      loading={isLoadingNeighborhoods}
      id="selected-neighborhood"
      options={neighborhoods || []}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'clear') {
          setAutocompleteValue(null);
          handleChange(null);
        }
      }}
      getOptionLabel={(option: INeighborhood) => {
        if (typeof option === 'string') {
          return option;
        }
        return `${option.name} Phase ${option.phase}`;
      }}
      renderOption={(props, option: INeighborhood) => {
        return (
          <li {...props} key={option.neighborhoodId}>
            {option.name} Phase {option.phase}
          </li>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          key={params.id}
          size="small"
          autoComplete="on"
          label={isLoadingNeighborhoods ? 'Loading...' : 'Neighborhood'}
          required={isRequired}
          disabled={isDisabled}
          variant="outlined"
        />
      )}
    />
  );
};
